import { getModuleLoadErrorUrl, isModuleLoadError } from '../utils/moduleLoadError';

export const STALE_ASSET_RELOAD_STORAGE_KEY = 'valentine.stale-asset-reload-v1';
export const STALE_ASSET_COOLDOWN_MS = 30_000;

export type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type AssetStatusProbe = (assetUrl: string) => Promise<number>;
export type StaleDeploymentProbe = (origin: string) => Promise<boolean>;

async function probeAssetStatus(assetUrl: string): Promise<number> {
  const response = await fetch(assetUrl, {
    method: 'HEAD',
    cache: 'no-store',
    credentials: 'same-origin',
  });
  return response.status;
}

/**
 * Probes the entry HTML to verify if the server is serving a newer deployment build.
 * Used when the browser does not provide the failed asset URL (Safari / WebKit).
 */
export async function probeStaleDeployment(origin: string): Promise<boolean> {
  if (typeof document === 'undefined') return false;
  const currentScript = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  const currentSrc = currentScript?.getAttribute('src');
  if (!currentSrc) return false;

  const cleanSrc = currentSrc.startsWith('http') ? new URL(currentSrc).pathname : currentSrc;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const path = pathname.startsWith('/tasks-app') ? '/tasks-app/' : '/';
  const baseOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const indexUrl = new URL(path, baseOrigin).toString();

  try {
    const response = await fetch(indexUrl, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return false;
    const html = await response.text();
    return !html.includes(cleanSrc);
  } catch {
    return false;
  }
}

function isSameOriginUrl(assetUrl: string, origin: string): boolean {
  try {
    return new URL(assetUrl, origin).origin === new URL(origin).origin;
  } catch {
    return false;
  }
}

const inFlightRecoveries = new Map<string, Promise<boolean>>();
let reloadInitiated = false;

export function clearRecoveryMarker(storage: RecoveryStorage = window.sessionStorage): void {
  reloadInitiated = false;
  inFlightRecoveries.clear();
  try {
    storage.removeItem(STALE_ASSET_RELOAD_STORAGE_KEY);
  } catch {
    // A blocked storage backend must not turn the error boundary into an error.
  }
}

function isCooldownActive(storage: RecoveryStorage, now = Date.now()): boolean {
  try {
    const raw = storage.getItem(STALE_ASSET_RELOAD_STORAGE_KEY);
    if (raw === null) return false;
    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) return true; // Non-numeric or legacy marker: treat as guarded
    return now - timestamp < STALE_ASSET_COOLDOWN_MS;
  } catch {
    return true; // Blocked storage: fail safe
  }
}

/**
 * Reload once only when a failed same-origin lazy asset is confirmed missing.
 *
 * Module-load messages alone can also represent a transient offline, proxy, or
 * CSP failure. A no-store probe that returns 404 is the evidence needed to
 * treat the error as a deployment mismatch rather than discard unsaved work.
 *
 * For Safari / WebKit engines that omit the asset URL ("Importing a module script failed"),
 * the entry HTML is probed to detect whether the active document has been superseded
 * by a newer deployment.
 */
export async function recoverFromStaleAssetError(
  error: unknown,
  storage: RecoveryStorage,
  reload: () => void,
  probe: AssetStatusProbe = probeAssetStatus,
  origin = typeof window !== 'undefined' ? window.location.origin : '',
  now = Date.now(),
  probeStale: StaleDeploymentProbe = probeStaleDeployment,
): Promise<boolean> {
  if (reloadInitiated) return true;
  if (!isModuleLoadError(error)) return false;

  const assetUrl = getModuleLoadErrorUrl(error);
  if (assetUrl && !isSameOriginUrl(assetUrl, origin)) return false;

  if (isCooldownActive(storage, now)) return false;

  const recoveryKey = `${origin}|${assetUrl ?? 'stale-deployment'}`;
  const existing = inFlightRecoveries.get(recoveryKey);
  if (existing) {
    return existing;
  }

  const recoveryPromise = (async () => {
    let isStale = false;

    if (assetUrl) {
      let status: number;
      try {
        status = await probe(assetUrl);
      } catch {
        return false;
      }
      isStale = status === 404;
    } else {
      // Browser omitted the asset URL (e.g. Safari / WebKit 'Importing a module script failed.').
      // Probe the entry HTML to confirm whether the deployment has updated.
      try {
        isStale = await probeStale(origin);
      } catch {
        return false;
      }
    }

    if (!isStale) {
      return false;
    }

    if (isCooldownActive(storage, now)) {
      return false;
    }

    try {
      storage.setItem(STALE_ASSET_RELOAD_STORAGE_KEY, String(now));
      reloadInitiated = true;
      reload();
      return true;
    } catch {
      return false;
    }
  })();

  inFlightRecoveries.set(recoveryKey, recoveryPromise);
  try {
    return await recoveryPromise;
  } finally {
    inFlightRecoveries.delete(recoveryKey);
  }
}
